import { useCallback, useEffect, useRef } from "react";
import { primeBrowserAudio } from "../browserAudio";
import type { PttTransport } from "./types";
import type { PttTurnSession } from "./usePttTurns";

const IDLE: PttTransport = {
  startRecording: () => undefined,
  stopRecording: () => undefined,
  isRecording: () => false,
  keyMode: "hold",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function speechRecognitionCtor(): (new () => any) | null {
  if (typeof window === "undefined") return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Chromium Web Speech fallback when Voicebox/AWS is offline. Firefox: use Voicebox. */
export function useBrowserSttTransport(enabled: boolean, session: PttTurnSession): PttTransport {
  const sessionRef = useRef(session);
  sessionRef.current = session;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);

  const stopRecording = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
  }, []);

  const startRecording = useCallback(() => {
    if (!enabled || sessionRef.current.busyRef.current) return;
    const Ctor = speechRecognitionCtor();
    if (!Ctor) {
      sessionRef.current.setError(
        "Firefox needs Voicebox for PTT. Run ./scripts/run_dev.sh and check /voicebox/health.",
      );
      return;
    }
    primeBrowserAudio();
    sessionRef.current.setRecording();
    sessionRef.current.setError(null);
    const r = new Ctor();
    r.continuous = true;
    r.interimResults = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    r.onresult = (e: any) => {
      const text = (e.results[e.results.length - 1]?.[0]?.transcript ?? "").trim();
      if (!text) return;
      sessionRef.current.busyRef.current = true;
      sessionRef.current.setProcessing();
      void sessionRef.current.handleResult(text).finally(() => {
        sessionRef.current.busyRef.current = false;
        sessionRef.current.setIdle();
      });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    r.onerror = (e: any) => {
      sessionRef.current.setError(`Mic: ${e.error}`);
      sessionRef.current.setIdle();
      sessionRef.current.busyRef.current = false;
    };
    r.onend = () => {
      recognitionRef.current = null;
      if (!sessionRef.current.busyRef.current) sessionRef.current.setIdle();
    };
    r.start();
    recognitionRef.current = r;
  }, [enabled]);

  useEffect(() => () => recognitionRef.current?.stop(), []);

  if (!enabled) return IDLE;

  return {
    startRecording,
    stopRecording,
    isRecording: () => recognitionRef.current !== null || session.pttState === "recording",
    keyMode: "hold",
  };
}
